import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Eye, MapPin as MapPinIcon } from 'lucide-react';

const PropertyMap = ({ properties, loading, mapViewState }) => {
  const navigate = useNavigate();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [selectedProperty, setSelectedProperty] = useState(null);

  // Get token from window
  const MAPBOX_TOKEN = window.REACT_APP_MAPBOX_TOKEN;

  // Initialize map - runs once on mount
  useEffect(() => {
    if (!MAPBOX_TOKEN || !mapContainer.current || map.current) return;
    if (typeof window.mapboxgl === 'undefined') {
      console.error('Mapbox GL JS not loaded from CDN');
      return;
    }

    window.mapboxgl.accessToken = MAPBOX_TOKEN;

    map.current = new window.mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [78.4867, 10.7905],
      zoom: 7,
    });

    map.current.addControl(new window.mapboxgl.NavigationControl(), 'top-left');
    map.current.addControl(new window.mapboxgl.FullscreenControl(), 'top-left');

    // Cleanup on unmount
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [MAPBOX_TOKEN]);

  // Update map view when mapViewState changes (zoom to location)
  useEffect(() => {
    if (!map.current || !mapViewState) return;

    const { latitude, longitude, zoom } = mapViewState;
    
    map.current.flyTo({
      center: [longitude, latitude],
      zoom: zoom || 12,
      duration: 1000,
    });
  }, [mapViewState]);

  // Add markers when properties change
  useEffect(() => {
    if (!map.current) return;

    // Remove existing markers
    const markers = document.querySelectorAll('.custom-marker');
    markers.forEach((el) => el.remove());

    if (!properties || properties.length === 0) {
      // Show "no properties" message on map
      return;
    }

    properties.forEach((property) => {
      if (!property.latitude || !property.longitude) return;

      const el = document.createElement('div');
      el.className = 'custom-marker';
      el.innerHTML = `
        <div class="bg-white px-3 py-1.5 rounded-full shadow-lg border-2 border-blue-500 cursor-pointer hover:scale-110 transition">
          <span class="text-xs font-bold text-blue-600">
            ₹${(property.selling_price / 100000).toFixed(1)}L
          </span>
        </div>
      `;

      el.addEventListener('click', () => {
        setSelectedProperty(property);
      });

      new window.mapboxgl.Marker(el)
        .setLngLat([parseFloat(property.longitude), parseFloat(property.latitude)])
        .addTo(map.current);
    });
  }, [properties]);

  if (loading) {
    return <div className="h-full w-full bg-neutral-100 animate-pulse" />;
  }

  if (!MAPBOX_TOKEN) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-neutral-50">
        <div className="text-center p-8 max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPinIcon className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900">Map Configuration Needed</h3>
          <p className="text-neutral-600 mt-2">Please add your Mapbox token</p>
        </div>
      </div>
    );
  }

  if (typeof window.mapboxgl === 'undefined') {
    return (
      <div className="h-full w-full flex items-center justify-center bg-neutral-50">
        <div className="text-center p-8 max-w-md">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPinIcon className="w-8 h-8 text-yellow-500" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900">Loading Map...</h3>
          <p className="text-neutral-600 mt-2">Please wait for Mapbox to load</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full relative">
      <div ref={mapContainer} className="h-full w-full" />

      {/* Popup overlay */}
      {selectedProperty && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 bg-white rounded-xl shadow-2xl p-4 max-w-xs w-full mx-4">
          <button
            onClick={() => setSelectedProperty(null)}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
          <h3 className="font-semibold text-neutral-900 text-base leading-tight line-clamp-2 pr-6">
            {selectedProperty.title}
          </h3>
          <p className="text-sm text-neutral-500 flex items-center gap-1 mt-1">
            <MapPinIcon className="w-3.5 h-3.5" />
            {selectedProperty.district}, {selectedProperty.city}
            {selectedProperty.village && `, ${selectedProperty.village}`}
          </p>
          <p className="text-xl font-bold text-primary-600 mt-2">
            ₹{Number(selectedProperty.selling_price).toLocaleString()}
          </p>
          <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-500 mt-2">
            <span className="capitalize px-2 py-0.5 bg-neutral-100 rounded-full">
              {selectedProperty.property_type}
            </span>
            {selectedProperty.bedrooms > 0 && <span>🛏️ {selectedProperty.bedrooms}</span>}
            {selectedProperty.bathrooms > 0 && <span>🚿 {selectedProperty.bathrooms}</span>}
            {selectedProperty.land_area > 0 && <span>📐 {selectedProperty.land_area} sqft</span>}
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => navigate(`/property/${selectedProperty.property_id}`)}
              className="flex-1 bg-primary-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-primary-600 transition"
            >
              <Eye className="w-4 h-4 inline mr-1" /> View Details
            </button>
            <button
              onClick={() => window.location.href = 'tel:+919159904385'}
              className="bg-green-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-green-600 transition"
            >
              <Phone className="w-4 h-4 inline" />
            </button>
          </div>
          <p className="text-[10px] text-neutral-400 text-center mt-2">
            📞 All inquiries handled by Nalla Brokers
          </p>
        </div>
      )}
    </div>
  );
};

export default PropertyMap;